/**
 * useFocusTrap Hook
 * =================
 * Traps keyboard focus within a modal/dialog container for WCAG 2.1 AA compliance.
 *
 * Features:
 * - Tab cycles through focusable elements within the container only
 * - Shift+Tab cycles in reverse
 * - Focus is moved into the container on mount
 * - Focus is restored to the previously focused element on unmount
 */

import { useEffect, useRef, RefObject } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  'iframe',
  'audio[controls]',
  'video[controls]',
  '[contenteditable]',
].join(', ');

export function useFocusTrap(containerRef: RefObject<HTMLElement | null>, isActive: boolean = true) {
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isActive) return;

    const container = containerRef.current;
    if (!container) return;

    // Save the previously focused element to restore later
    previouslyFocusedRef.current = document.activeElement as HTMLElement;

    // Move focus into the container after a brief delay to allow rendering
    const initialFocusTimeout = setTimeout(() => {
      const focusableElements = container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (focusableElements.length > 0) {
        focusableElements[0].focus();
      } else {
        // If no focusable children, make the container itself focusable
        container.setAttribute('tabindex', '-1');
        container.focus();
      }
    }, 0);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      const focusableElements = container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (focusableElements.length === 0) return;

      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        // Shift+Tab: if at the first element, wrap to the last
        if (document.activeElement === firstFocusable || !container.contains(document.activeElement)) {
          e.preventDefault();
          lastFocusable.focus();
        }
      } else {
        // Tab: if at the last element, wrap to the first
        if (document.activeElement === lastFocusable || !container.contains(document.activeElement)) {
          e.preventDefault();
          firstFocusable.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      clearTimeout(initialFocusTimeout);
      document.removeEventListener('keydown', handleKeyDown);

      // Restore focus to the previously focused element
      if (previouslyFocusedRef.current && typeof previouslyFocusedRef.current.focus === 'function') {
        previouslyFocusedRef.current.focus();
      }
    };
  }, [isActive, containerRef]);
}

export default useFocusTrap;
